<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import ModalCheckout from '@satch-ui/components/modal/ModalCheckout.vue'
import { t } from '@satch-ui/translations/translations'
import CarouselItem from '@shared/fondof-ui/components/molecules/carousel/CarouselItem.vue'
import CarouselRoot from '@shared/fondof-ui/components/molecules/carousel/CarouselRoot.vue'
import CarouselScrollContainer from '@shared/fondof-ui/components/molecules/carousel/CarouselScrollContainer.vue'
import CarouselViewport from '@shared/fondof-ui/components/molecules/carousel/CarouselViewport.vue'
import GalleryThumbnails from '@storefront-satch/components/molecules/gallery/GalleryThumbnails.vue'
import GalleryVideo from '@storefront-satch/components/molecules/gallery/GalleryVideo.vue'
import GalleryZoomImage from '@storefront-satch/components/molecules/gallery/GalleryZoomImage.vue'
import type { MediaItem } from '@storefront-satch/components/molecules/gallery/types'
import type { EmblaCarouselType } from 'embla-carousel'

const props = defineProps<{
  media: MediaItem[]
  activeIndex: number
  isOpen: boolean
}>()

const emit = defineEmits<{
  onSlideChange: [api: EmblaCarouselType | undefined]
}>()

const mainCarousel = ref<EmblaCarouselType | undefined>(undefined)
const thumbsCarousel = ref<EmblaCarouselType | undefined>(undefined)
const zoomActive = ref<boolean>(false)
const dragActive = ref<boolean>(true)
const isHintVisible = ref<boolean>(false)
const galleryZoomModalContent = ref<HTMLElement | null>(null)
const hasShownHintOnce = ref<boolean>(false)

const carouselOptions = computed(() => {
  const options = {
    slidesToScroll: 1,
    loop: true,
    watchDrag: dragActive.value,
  }

  return options
})

const setSliderAllowTouchMove = (value: boolean) => {
  dragActive.value = value
}

const setThumbsCarousel = (carousel: EmblaCarouselType | undefined) => {
  thumbsCarousel.value = carousel
}

const setMainCarousel = (carousel: EmblaCarouselType | undefined) => {
  mainCarousel.value = carousel

  if (carousel) {
    carousel.on('pointerDown', () => {
      setIsHintVisible(false)
    })

    carousel.scrollTo(props.activeIndex, true)
  }
}

const setZoomActive = (value: boolean) => {
  zoomActive.value = value
}

const setIsHintVisible = (value: boolean) => {
  isHintVisible.value = value
}

const scrollToIndex = (index: number) => {
  if (mainCarousel.value) {
    mainCarousel.value.scrollTo(index)
    setZoomActive(false)
  }
}

const onSlideChange = (api: EmblaCarouselType | undefined) => {
  thumbsCarousel.value?.scrollTo(api?.selectedScrollSnap() || 0)
  emit('onSlideChange', api)
}

const onPinchActive = (value: boolean) => setSliderAllowTouchMove(!value)

onMounted(() => {
  if (mainCarousel.value) {
    mainCarousel.value?.on('select', () => {
      setZoomActive(false)
    })
  }
})

watch(
  zoomActive,
  (value) => {
    if (mainCarousel.value) {
      setSliderAllowTouchMove(!value)
    }
  },
  { immediate: true }
)

watch(thumbsCarousel, (value) => {
  if (value && mainCarousel.value) {
    setSliderAllowTouchMove(true)
  }
})

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      if (!hasShownHintOnce.value) {
        setIsHintVisible(true)
        hasShownHintOnce.value = true
      }

      if (mainCarousel.value) {
        mainCarousel.value.scrollTo(props.activeIndex, true)
      }
    }
  }
)
</script>

<template>
  <ModalCheckout
    :is-open="props.isOpen"
    :has-container-padding="false"
  >
    <template #contentCenter>
      <div
        ref="galleryZoomModalContent"
        class="flex h-full flex-col items-center"
        @click="setIsHintVisible(false)"
      >
        <div class="relative flex size-full min-h-0 flex-1 items-center">
          <Transition
            name="fade-in"
            appear
          >
            <div
              v-if="isHintVisible"
              class="text-primary text-md shadow-light-top pointer-events-none absolute left-1/3 top-1/2 z-10 w-2/3 -translate-x-1/4 -translate-y-1/2 rounded-[500px] bg-white p-5 text-center"
            >
              {{ t('productDetail.zoomHint') }}
            </div>
          </Transition>

          <CarouselRoot
            v-if="media.length"
            class="h-full"
            :options="carouselOptions"
            @active-index-change="onSlideChange"
            @embla-api="setMainCarousel"
          >
            <CarouselViewport class="h-full">
              <CarouselScrollContainer class="h-full">
                <CarouselItem
                  v-for="(item, index) in media"
                  :key="index"
                  class="size-full justify-center overflow-hidden pt-10"
                >
                  <GalleryZoomImage
                    v-if="item.mediaType === 'image'"
                    :item="item"
                    :zoom-active-inherited="zoomActive"
                    @zoom-enabled="setZoomActive(true)"
                    @zoom-disabled="setZoomActive(false)"
                    @pinch-active="onPinchActive"
                  />

                  <GalleryVideo
                    v-if="item.mediaType === 'video'"
                    :item="item"
                    :is-active="index === activeIndex"
                    video-class="max-w-full max-h-full"
                  />
                </CarouselItem>
              </CarouselScrollContainer>
            </CarouselViewport>
          </CarouselRoot>
        </div>

        <div
          v-if="props.media.length"
          class="shadow-light-top w-full bg-white py-4"
        >
          <GalleryThumbnails
            :slides-to-scroll="1"
            :scrollbar="true"
            :active-index="props.activeIndex"
            thumbnail-active-class="opacity-100 border-2 border-primary"
            thumbnail-height="15vw"
            thumbnail-width="15vw"
            space-between="10px"
            scroll-container-class="px-4"
            :media="props.media"
            @embla-api="setThumbsCarousel"
            @clicked="scrollToIndex"
          />
        </div>
      </div>
    </template>
  </ModalCheckout>
</template>
